'use client';

import { Badge, Button, Label, ProgressBar, SelectTags, Typography } from 'components';
import {
  CloseCircleIcon,
  CloseIcon,
  FileDoneIcon,
  FileErrorIcon,
  FileProcessingIcon,
  GreenIcon,
  PlusIcon,
  RetryIcon
} from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

type StatusCardFile =
  | 'added'
  | 'processed'
  | 'processing'
  | 'error-in-type'
  | 'error-in-size'
  | 'error-in-upload';
export interface IFileContextLibrary {
  id: string;
  status?: StatusCardFile;
  filename?: string;
  filesize?: string;
  percentage?: number | null;
  tags?: string[];
  defaultTags?: string[];
  file?: File;
}

interface ICardFile extends IFileContextLibrary {
  onUpdateTags?: (tags: string[]) => void;
  onRemove?: (id: string) => void;
  onRetry: (cardFile: IFileContextLibrary) => void;
}

const CardFile: FC<ICardFile> = ({
  id,
  percentage: percentageIncoming,
  status,
  filename,
  filesize,
  file,
  tags = [],
  defaultTags = [],
  onUpdateTags,
  onRemove,
  onRetry
}) => {
  const t = useTranslations();
  const [openTags, setOpenTags] = useState(false);
  const [percentage, setPercentage] = useState<number | null>(null);

  const isError = status?.includes('error');
  const isErrorInType = status === 'error-in-type';
  const isErrorInSize = status === 'error-in-size';
  const isErrorInUpload = status === 'error-in-upload';
  const isAdded = status === 'added';
  const isProcessing = status === 'processing';
  const isProcessed = status === 'processed';

  const isReady = isProcessing || isProcessed || isAdded;

  const showProgressBar = (isProcessing || isProcessed) && !Number.isNaN(Number(percentage));

  const filteredDefaultTags = useMemo(
    () => defaultTags.filter((tag) => !tags.includes(tag)),
    [defaultTags, tags]
  );

  const optionsDefaultTags = filteredDefaultTags?.map((tag) => ({ label: tag, value: tag }));

  const handleOpenTags = () => setOpenTags(true);
  const handleCloseTags = () => setOpenTags(false);

  const handleRemove = useCallback(() => onRemove?.(id), [id, onRemove]);

  const handleRetry = () =>
    onRetry({
      id,
      file,
      status: 'added',
      percentage: 0,
      filename: filename,
      filesize: filesize
    });

  const handleAddTags = useCallback(
    (tag: string) => {
      if (tag && !tags?.includes(tag)) onUpdateTags?.([...tags, tag]);
    },
    [tags]
  );

  const handleOnChangeTags = (tagsChanged?: string[]) => {
    if (tagsChanged) onUpdateTags?.(tagsChanged);
  };

  const handleAddSuggestedUpdateTags = (tag: string) => () => handleAddTags(tag);

  const handleDeleteTags = (tagRemoved: string) =>
    onUpdateTags?.(tags?.filter((tag) => tag !== tagRemoved));

  useEffect(() => {
    if (isProcessed) setOpenTags(false);
  }, [isProcessed]);

  useEffect(() => {
    const percentageNumber = Number(percentageIncoming);
    if (!Number.isNaN(percentageNumber)) setPercentage(percentageNumber);
  }, [percentageIncoming]);

  useEffect(() => {
    setTimeout(() => {
      if (percentage !== null && isProcessing)
        setPercentage((oldState) => {
          const result = (oldState as number) ** (1 + Math.random() / 3) + 10;
          return (oldState as number) < 90 && result < 100 ? Math.round(result) : oldState;
        });
    }, 1000);
  }, [percentage, isProcessing]);

  return (
    <div className="relative flex flex-col rounded-[8px] border border-tertiary-600 p-4">
      <div className="absolute right-2 top-2 z-[1000]">
        <Button
          size="small"
          variant="icon"
          color={isProcessed ? 'primary' : 'neutral'}
          className="!w-8 rounded-full"
          onClick={handleRemove}
          startAdornment={
            isProcessed ? (
              <GreenIcon className="scale-75" />
            ) : (
              <CloseCircleIcon className="scale-75" />
            )
          }
        />
      </div>
      <div className="flex">
        <div className="pr-4">
          {isError && <FileErrorIcon className="pl-[2px]" />}
          {(isProcessing || isAdded) && <FileProcessingIcon />}
          {isProcessed && <FileDoneIcon className="pl-[2px]" />}
        </div>
        <div className="flex flex-1 flex-col">
          {isError && (
            <Typography variant="light4" className="mr-4 pb-[2px] !leading-4 text-secondary-red">
              {isErrorInType && <>{t('context_library.card_file.error_invalid_type.title')}</>}
              {isErrorInSize && <>{t('context_library.card_file.error_invalid_size.title')}</>}
              {isErrorInUpload && <>{t('context_library.card_file.error_upload.title')}</>}
            </Typography>
          )}
          <Typography variant="regular4" className="mr-4 pb-[2px] !leading-5 text-tertiary-100">
            {filename}
          </Typography>
          <div className="flex gap-4">
            {isReady && (
              <div className="flex">
                <Typography variant="light4" className="!leading-4 text-tertiary-300">
                  {t('context_library.card_file.file_size')}:
                </Typography>
                <Typography variant="regular4" className="ml-2 !leading-4 text-tertiary-200">
                  {filesize}
                </Typography>
              </div>
            )}
          </div>
          {isReady && !openTags && (
            <div className="flex justify-end">
              <div className="flex w-4/6 flex-wrap justify-end gap-2">
                {tags?.map((tag) => (
                  <Badge
                    key={tag}
                    className="relative h-6 !bg-primary-blue-900"
                    classNameTypography="text-primary-blue-100 ml-1 !text-xs"
                  >
                    {tag}
                  </Badge>
                ))}
                {!isProcessed && (
                  <Badge
                    icon={<PlusIcon className="scale-[1.2]" />}
                    className="relative h-6 !bg-primary-blue-900"
                    classNameTypography="text-primary-blue-100 ml-1 !text-xs"
                    onClick={handleOpenTags}
                  >
                    {t('context_library.card_file.add_tags')}
                  </Badge>
                )}
              </div>
            </div>
          )}
          {isError && (
            <Typography variant="light5" className="pt-2 text-tertiary-200">
              {isErrorInType && <>{t('context_library.card_file.error_invalid_type.paragraph')}</>}
              {isErrorInSize && <>{t('context_library.card_file.error_invalid_size.paragraph')}</>}
              {isErrorInUpload && <>{t('context_library.card_file.error_upload.paragraph')}</>}
            </Typography>
          )}
          {isErrorInUpload && (
            <Button
              variant="icon"
              size="small"
              colorIcon="blue"
              startAdornment={<RetryIcon />}
              classNameTypography="text-secondary-link"
              label={t('context_library.card_file.retry')}
              onClick={handleRetry}
            />
          )}
          {showProgressBar && (
            <div className="mt-2 flex items-center">
              <ProgressBar
                hideNumber
                height="h-2"
                color="bg-primary-blue-100"
                percentage={percentage ?? 0}
                className="w-[90%]"
              />
              <Typography
                variant="light4"
                className="w-[10%] text-right !leading-3 text-tertiary-100"
              >
                {percentage}%
              </Typography>
            </div>
          )}
        </div>
      </div>
      {openTags && !isProcessed && (
        <div className="w-full">
          <Label className="mt-0">{t('labels.tags')}</Label>
          <SelectTags
            options={optionsDefaultTags}
            placeholder={t('placeholders.add_tags')}
            placeholderSearch={t('placeholders.search_tags')}
            onAddTag={handleAddTags}
            onChange={handleOnChangeTags}
            onDelete={handleDeleteTags}
            selectedOptions={tags}
            classNameTags="!bg-primary-blue-900 !rounded-full"
            classNameTagsTypography="!text-primary-blue-100"
            noClose
          />
          {!!filteredDefaultTags?.length && (
            <div className="relative mt-2 flex flex-col rounded-3xl border border-tertiary-600 bg-tertiary-800 px-2">
              <div className="absolute -top-1 right-[1px] z-[0]">
                <Button
                  size="small"
                  variant="icon"
                  color={isProcessed ? 'primary' : 'neutral'}
                  className="!w-8 rounded-full"
                  onClick={handleCloseTags}
                  startAdornment={<CloseIcon className="scale-75" />}
                />
              </div>
              <Label className="mt-0">{t('labels.suggested_tags')}</Label>
              <div className="mb-2 flex flex-wrap gap-2">
                {filteredDefaultTags?.map((tag) => (
                  <Badge
                    key={tag}
                    className="relative h-6 !bg-tertiary-600"
                    classNameTypography="text-tertiary-200 ml-1 !text-xs"
                    onClick={handleAddSuggestedUpdateTags(tag)}
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CardFile;
